export type ResolvedDate = {
  year: number
  month?: number
}

/**
 * Resolves common Swedish time phrases to a year and optional month.
 * Returns undefined if the phrase cannot be resolved with reasonable confidence.
 */
export function resolveTimeText(
  timeText: string,
  referenceDate: Date = new Date(),
  birthDate?: Date,
): ResolvedDate | undefined {
  const text = timeText.toLowerCase().trim()
  const refYear = referenceDate.getFullYear()
  const refMonth = referenceDate.getMonth() + 1 // 1-based

  // Age-based phrases: "när hon/han/hen var X", "vid X års ålder", "som X-åring", "sedan X" (when birthDate known)
  if (birthDate) {
    const birthYear = birthDate.getFullYear()

    const swedishNumbers: Record<string, number> = {
      ett: 1, två: 2, tre: 3, fyra: 4, fem: 5, sex: 6, sju: 7, åtta: 8, nio: 9, tio: 10,
      elva: 11, tolv: 12, tretton: 13, fjorton: 14, femton: 15, sexton: 16,
      sjutton: 17, arton: 18, nitton: 19, tjugo: 20,
    }
    const numberWord = Object.keys(swedishNumbers).join('|')
    const ageCapture = `(\\d+|${numberWord})`

    const parseAge = (raw: string): number => {
      const n = parseInt(raw, 10)
      return isNaN(n) ? (swedishNumbers[raw] ?? 0) : n
    }

    const agePatterns = [
      new RegExp(`^(?:när|då)\\s+(?:hon|han|hen|personen|barnet)\\s+var\\s+${ageCapture}`),
      new RegExp(`^vid\\s+${ageCapture}\\s+års?\\s+ålder`),
      new RegExp(`^som\\s+${ageCapture}-åring`),
      new RegExp(`^sedan\\s+(?:hon|han|hen|personen|barnet)\\s+var\\s+${ageCapture}`),
      new RegExp(`^från\\s+${ageCapture}\\s+års?\\s+ålder`),
      new RegExp(`^i\\s+${ageCapture}-årsåldern`),
      new RegExp(`^(?:runt|kring|vid)\\s+${ageCapture}\\s+år`),
    ]

    for (const pattern of agePatterns) {
      const match = text.match(pattern)
      if (match?.[1]) {
        const age = parseAge(match[1])
        if (age > 0) return { year: birthYear + age }
      }
    }
  }

  // "idag" / "i dag"
  if (/^i ?dag$/.test(text)) {
    return { year: refYear, month: refMonth }
  }

  // "igår" / "i går"
  if (/^i ?går$/.test(text)) {
    const d = new Date(referenceDate)
    d.setDate(d.getDate() - 1)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
  }

  // "i år" / "i år"
  if (/^i år$/.test(text)) {
    return { year: refYear }
  }

  // "förra året" / "förra år"
  if (/^förra (året|år)$/.test(text)) {
    return { year: refYear - 1 }
  }

  // "förra månaden" / "förra månad"
  if (/^förra (månaden|månad)$/.test(text)) {
    const d = new Date(referenceDate)
    d.setMonth(d.getMonth() - 1)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
  }

  // "förra veckan" / "förra vecka"
  if (/^förra (veckan|vecka)$/.test(text)) {
    const d = new Date(referenceDate)
    d.setDate(d.getDate() - 7)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
  }

  // "i somras" / "förra sommaren"
  if (/^(i somras|förra sommaren)$/.test(text)) {
    return { year: refMonth >= 9 ? refYear : refYear - 1, month: 7 }
  }

  // "i vintras" / "förra vintern"
  if (/^(i vintras|förra vintern)$/.test(text)) {
    return { year: refMonth >= 3 ? refYear - 1 : refYear - 2, month: 1 }
  }

  // "i våras" / "förra våren"
  if (/^(i våras|förra våren)$/.test(text)) {
    return { year: refMonth >= 6 ? refYear : refYear - 1, month: 4 }
  }

  // "i höstas" / "förra hösten"
  if (/^(i höstas|förra hösten)$/.test(text)) {
    return { year: refMonth >= 12 ? refYear : refYear - 1, month: 10 }
  }

  // "för X månader sedan"
  const monthsAgoMatch = text.match(/^för (\d+) månader? sedan$/)
  if (monthsAgoMatch) {
    const months = parseInt(monthsAgoMatch[1]!, 10)
    const d = new Date(referenceDate)
    d.setMonth(d.getMonth() - months)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
  }

  // "för X år sedan"
  const yearsAgoMatch = text.match(/^för (\d+) år sedan$/)
  if (yearsAgoMatch) {
    const years = parseInt(yearsAgoMatch[1]!, 10)
    return { year: refYear - years }
  }

  // "för X veckor sedan"
  const weeksAgoMatch = text.match(/^för (\d+) veckor? sedan$/)
  if (weeksAgoMatch) {
    const weeks = parseInt(weeksAgoMatch[1]!, 10)
    const d = new Date(referenceDate)
    d.setDate(d.getDate() - weeks * 7)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
  }

  // "nästa år"
  if (/^nästa år$/.test(text)) {
    return { year: refYear + 1 }
  }

  // "nästa månad"
  if (/^nästa månad$/.test(text)) {
    const d = new Date(referenceDate)
    d.setMonth(d.getMonth() + 1)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
  }

  // "i [month name]" e.g. "i januari", "i mars"
  const monthNames: Record<string, number> = {
    januari: 1, februari: 2, mars: 3, april: 4, maj: 5, juni: 6,
    juli: 7, augusti: 8, september: 9, oktober: 10, november: 11, december: 12,
  }
  const monthNameMatch = text.match(/^i (januari|februari|mars|april|maj|juni|juli|augusti|september|oktober|november|december)$/)
  if (monthNameMatch?.[1]) {
    const month = monthNames[monthNameMatch[1]]
    if (month) {
      const year = month > refMonth ? refYear - 1 : refYear
      return { year, month }
    }
  }

  return undefined
}

export const MONTH_NAMES_SV: Record<number, string> = {
  1: 'Januari', 2: 'Februari', 3: 'Mars', 4: 'April', 5: 'Maj', 6: 'Juni',
  7: 'Juli', 8: 'Augusti', 9: 'September', 10: 'Oktober', 11: 'November', 12: 'December',
}
